<template>
  <section
    v-if="buckets.length > 0"
    class="capex-trend"
    data-test="capex-trend"
  >
    <header class="ct-head">
      <div>
        <p class="ct-eyebrow">Capitalization · monthly</p>
        <h2 class="ct-title">CapEx share by month</h2>
      </div>
      <span
        class="ct-info"
        :title="tooltip"
        aria-label="How the monthly CapEx share is derived"
        tabindex="0"
      >&#9432;</span>
    </header>

    <p class="ct-sub">
      Each sprint counts in the month it <strong>ends</strong>; bars show
      capitalizable tickets ÷ all classifiable tickets that month.
    </p>

    <div class="ct-rows">
      <div
        v-for="b in buckets"
        :key="b.month"
        class="ct-row"
        :data-test="`capex-month-${b.month}`"
      >
        <span class="ct-month">{{ b.label }}</span>
        <div class="ct-bar-wrap">
          <div
            class="ct-bar"
            :style="{ width: barWidth(b.countPct) }"
            :class="{ 'is-empty': b.countPct == null }"
          />
        </div>
        <span class="ct-pct" data-test="capex-month-count-pct">{{ pctLabel(b.countPct) }}</span>
        <span class="ct-detail">
          {{ pctLabel(b.spPct) }} by SP ·
          {{ b.capexCount }}/{{ b.totalCount }} tickets ·
          {{ b.sprintCount }} sprint{{ b.sprintCount === 1 ? "" : "s" }}
        </span>
      </div>
    </div>

    <p class="ct-foot">
      Headline % is ticket-count based (what finance reports today); the
      story-point weighted figure beside it is a better effort proxy.
    </p>
  </section>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { capexByMonth } from "@/lib/capexByMonth";
import type { SprintListItem } from "@/api/types";

const props = defineProps<{
  sprints: SprintListItem[];
}>();

const buckets = computed(() => capexByMonth(props.sprints));

const tooltip =
  "Capitalization is computed per sprint, then bucketed into the calendar month " +
  "the sprint ends in. Percentages are recomputed from summed CapEx/total across " +
  "the month's sprints — not averaged. This is \"CapEx of sprints ending in month " +
  "X\", an approximation of calendar-month accrual.";

function pctLabel(p: number | null | undefined): string {
  return typeof p === "number" ? `${Math.round(p * 100)}%` : "—";
}

function barWidth(p: number | null): string {
  return p == null ? "0%" : `${Math.round(p * 100)}%`;
}
</script>

<style scoped>
.capex-trend {
  background: var(--bg-elev);
  border: 1px solid var(--line);
  border-radius: 16px;
  padding: 24px 28px;
  display: grid;
  gap: 8px;
  min-width: 0;
}
.ct-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; }
.ct-eyebrow {
  font-size: 11px; font-weight: 600; text-transform: uppercase;
  letter-spacing: 0.06em; color: var(--fg-subtle, #8a8f9c); margin: 0 0 4px;
}
.ct-title { font-size: 18px; font-weight: 500; letter-spacing: -0.02em; margin: 0; }
.ct-info { color: var(--muted); cursor: help; font-size: 14px; line-height: 1; }
.ct-sub { font-size: 13px; color: var(--ink-2); margin: 2px 0 10px; line-height: 1.5; }

.ct-rows { display: grid; gap: 10px; }
.ct-row {
  display: grid;
  grid-template-columns: 84px minmax(60px, 1fr) 48px;
  grid-template-areas: "month bar pct" "detail detail detail";
  align-items: center;
  gap: 4px 12px;
}
.ct-month { grid-area: month; font-size: 13px; font-weight: 500; color: var(--ink); }
.ct-bar-wrap {
  grid-area: bar; height: 8px; border-radius: 999px;
  background: color-mix(in srgb, var(--ink) 8%, transparent); overflow: hidden;
}
.ct-bar {
  height: 100%; border-radius: 999px; background: var(--brand);
  transition: width var(--speed-quick, 0.2s) var(--ease, ease);
  min-width: 2px;
}
.ct-bar.is-empty { background: transparent; }
.ct-pct { grid-area: pct; text-align: right; font-size: 14px; font-weight: 600; color: var(--ink); font-variant-numeric: tabular-nums; }
.ct-detail { grid-area: detail; font-size: 11px; color: var(--muted); }

.ct-foot { font-size: 12px; color: var(--muted); margin: 6px 0 0; line-height: 1.5; }
</style>
